/**
 * data/quests/main — the main story spine: the 8-badge Badge Circuit + the
 * finale of the Mewtwo Mystery (main-quest.md). Every quest obeys the triple
 * contract: badges never gate the cozy loop, nothing is timed or missable, a gym
 * loss is no-stakes, and the whole line is ignorable.
 *
 * Each badge quest runs: a gentle WARM-UP stage that teaches the biome, then the
 * gym battle itself (`defeat-gym-leader`, keyed to the leader's gymId). Availability
 * chains softly on the PRIOR badge quest being turned in (visibility only — never a
 * locked door). The finale opens once Badge 8's quest is done (== 8 badges).
 *
 * NOTE (frozen-contract deviation): shared/quest.ts allows exactly ONE `reward` per
 * quest. main-quest.md lists several rewards per badge (badge + codex + candies …).
 * We encode the DEFINING reward (the badge) and record the doc's secondary rewards
 * in an `INTENDED EXTRAS` comment so the session's badge-grant path (or a future
 * `reward: QuestReward[]` contract rev) can deliver them. See the return summary.
 */
import type { ObjectiveDef, QuestDef, QuestStage, StageDialogue } from '@/shared';

function stage(
  id: string,
  objectives: readonly ObjectiveDef[],
  onStart: readonly string[],
  onComplete: readonly string[],
): QuestStage {
  const dialogue: StageDialogue = { onStart, onComplete };
  return { id, objectives, dialogue };
}

export const MAIN_QUESTS: readonly QuestDef[] = [
  // ── Badge 1 · Meadow Badge (Normal) · Rowan ──
  // INTENDED EXTRAS (main-quest.md): + codex-unlock region-history + rare-candies 3.
  {
    id: 'main-badge-1',
    kind: 'main',
    title: 'The Meadow Badge',
    giver: 'npc-leader-meadow',
    synopsis: 'Rowan the Meadowkeeper offers your first friendly measure — no rush at all.',
    available: { kind: 'always' },
    tone: ['warm', 'unhurried'],
    reward: { kind: 'badge', badgeId: 'meadow-badge' },
    stages: [
      stage(
        'stage-1',
        [
          { kind: 'visit-biome', biomeId: 'meadow' },
          { kind: 'catch-count', count: 1 },
        ],
        [
          'Ease into the meadow first, friend — walk it, meet someone, catch a wild one if the mood takes you. No rush.',
        ],
        [
          'There — now you’ve the feel of the place. Come find me by the day-camp when you’re ready.',
        ],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'meadow-badge' }],
        ["A gym's just a friendly measure of how well you two listen. Whenever you like."],
        ['Beautifully done. The Meadow Badge is yours, and gladly given.'],
      ),
    ],
  },

  // ── Badge 2 · Rustle Badge (Bug) · Tansy ──
  // INTENDED EXTRAS: + evolution-stone leaf-stone.
  {
    id: 'main-badge-2',
    kind: 'main',
    title: 'The Rustle Badge',
    giver: 'npc-leader-forest',
    synopsis:
      'Tansy, who talks to beetles, would love you to meet the forest’s braver little folk.',
    available: { kind: 'quest-done', ref: 'main-badge-1' },
    tone: ['warm', 'giggly'],
    reward: { kind: 'badge', badgeId: 'forest-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'catch-type', monType: 'bug', count: 2 }],
        ['Would you catch a couple of bug friends first? They’re shy, but oh, they’re brave.'],
        ['Two new beetle-hellos! They liked you, I can tell. Now — a friendly round?'],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'forest-badge' }],
        ['Hear that? My whole team saying hello. Be gentle and they’ll be gentle back.'],
        ['Oh, they LIKED you! The Rustle Badge is yours, brave heart.'],
      ),
    ],
  },

  // ── Badge 3 · Ripple Badge (Water) · Marlow ──
  // INTENDED EXTRAS: + gift-mon poliwag L15.
  {
    id: 'main-badge-3',
    kind: 'main',
    title: 'The Ripple Badge',
    giver: 'npc-leader-lake',
    synopsis: 'Marlow of the Reeds shares the whole quiet sport of patience by the still water.',
    available: { kind: 'quest-done', ref: 'main-badge-2' },
    tone: ['warm', 'calm'],
    reward: { kind: 'badge', badgeId: 'lake-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'catch-species', speciesId: 'magikarp' }],
        [
          'Everyone’s first catch by the lake is a Magikarp — go on, land one. It’s tradition, warmly kept.',
        ],
        [
          'There it is, splashing away — welcome to the club. Patience suits you. Ready for a cast at me?',
        ],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'lake-badge' }],
        ['Cast, wait, and let the lake decide. Your team’s got that patient look — let’s see it.'],
        ['Steady as still water. The Ripple Badge — you’ve earned every ring of it.'],
      ),
    ],
  },

  // ── Badge 4 · Echo Badge (Rock/Ground) · Bruno Cobble ──
  // INTENDED EXTRAS: + evolution-stone moon-stone.
  {
    id: 'main-badge-4',
    kind: 'main',
    title: 'The Echo Badge',
    giver: 'npc-leader-cavern',
    synopsis:
      'Bruno the lamplighter will leave a light on in the deep — grow a little and come down.',
    available: { kind: 'quest-done', ref: 'main-badge-3' },
    tone: ['warm', 'gentle'],
    reward: { kind: 'badge', badgeId: 'cavern-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'reach-level', level: 22 }],
        [
          'Bring a team that’s found its feet — get someone to about level 22. Whole-team growth makes it easy, mind.',
        ],
        ['Sure-footed now, the lot of you. Come on down — I’ll leave a light on.'],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'cavern-badge' }],
        ['Trust your footing and your friends. Nothing to fear in the deep.'],
        [
          'Sure-footed and sure-hearted. The Echo Badge is yours — carry a bit of my lamplight with it.',
        ],
      ),
    ],
  },

  // ── Badge 5 · Verdant Badge (Grass) · Sage Fern ──
  // INTENDED EXTRAS: + codex-unlock the-lantern-keeper.
  {
    id: 'main-badge-5',
    kind: 'main',
    title: 'The Verdant Badge',
    giver: 'npc-leader-deepforest',
    synopsis: 'Sage Fern, the old-growth warden, celebrates a bond that has quietly grown.',
    available: { kind: 'quest-done', ref: 'main-badge-4' },
    tone: ['warm', 'contemplative'],
    reward: { kind: 'badge', badgeId: 'deep-forest-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'evolve', count: 1 }],
        [
          'Let one of yours grow into what it’s becoming — an evolution, whenever it’s ready. The deep wood keeps its own time.',
        ],
        ['There — did you feel that? A bond deepening. That’s the whole secret. Come, then.'],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'deep-forest-badge' }],
        ['You’ve been at this a while now. It shows — in the good way. Show me what’s grown.'],
        ['Deep roots, the both of you. The Verdant Badge suits a bond like that.'],
      ),
    ],
  },

  // ── Badge 6 · Summit Badge (Fighting) · Kestrel ──
  // INTENDED EXTRAS: + rare-candies 5.
  {
    id: 'main-badge-6',
    kind: 'main',
    title: 'The Summit Badge',
    giver: 'npc-leader-mountain',
    synopsis: 'Kestrel of the High Crags wants a few friendly rounds before the climb.',
    available: { kind: 'quest-done', ref: 'main-badge-5' },
    tone: ['warm', 'beaming'],
    reward: { kind: 'badge', badgeId: 'mountain-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'win-friendly-battles', count: 3 }],
        [
          'Spar the mountain folk first — three friendly rounds, no stakes, all fun. Warm up the legs!',
        ],
        ['Three clean rounds! You’re ready for the ridge, champ. Come on up.'],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'mountain-badge' }],
        ['Whole mountain to ourselves and the wind for a crowd! Ready when you are.'],
        ['HA! Top of the world, you two! The Summit Badge, and the best view in the region.'],
      ),
    ],
  },

  // ── Badge 7 · Relic Badge (Psychic/Ghost) · Enigma — seeds Mewtwo Beat D ──
  // INTENDED EXTRAS: + codex-unlock the-hollow-lab (Beat D) + gift-mon abra L30.
  {
    id: 'main-badge-7',
    kind: 'main',
    title: 'The Relic Badge',
    giver: 'npc-leader-ruins',
    synopsis:
      'Enigma, keeper of the murals, reads the restless stones — and hints at a room below.',
    available: { kind: 'quest-done', ref: 'main-badge-6' },
    tone: ['warm', 'mysterious'],
    reward: { kind: 'badge', badgeId: 'ruins-badge' },
    stages: [
      stage(
        'stage-1',
        [
          { kind: 'visit-biome', biomeId: 'old-ruins' },
          { kind: 'catch-species', speciesId: 'unown' },
        ],
        [
          'Walk the ruins a while, and find me an Unown — the stones speak through them. They’ve been… restless lately.',
        ],
        ['You heard the script too, didn’t you. Good. The stones trust you. Now — our battle.'],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'ruins-badge' }],
        ['Show me what you’ve built. The murals are watching.'],
        [
          'The stones approve — I felt them settle. The Relic Badge is yours, wanderer. Stay… there’s a room below I should tell you about.',
        ],
      ),
    ],
  },

  // ── Badge 8 · Ember Badge (Fire) · Cinder Vale — clearing it opens the finale ──
  // INTENDED EXTRAS: + town-prop eight-lanterns + codex-unlock the-circuit-walked.
  {
    id: 'main-badge-8',
    kind: 'main',
    title: 'The Ember Badge',
    giver: 'npc-leader-ashland',
    synopsis:
      'Cinder Vale, the ashwalker, tends the volcano’s shrine — and your last, warmest battle.',
    available: { kind: 'quest-done', ref: 'main-badge-7' },
    tone: ['warm', 'triumphant'],
    reward: { kind: 'badge', badgeId: 'ashland-badge' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'reach-level', level: 50 }],
        [
          'Bring your strongest heart up the ash — someone at about level 50. You didn’t come all this way to be nervous now.',
        ],
        [
          'Look at that team of yours. Ready as ready gets. Come to the shrine when the fire calls you.',
        ],
      ),
      stage(
        'stage-2',
        [{ kind: 'defeat-gym-leader', gymId: 'ashland-badge' }],
        ['Last badge, and the hardest. Let’s make it a battle they’ll tell stories about.'],
        [
          'STORIES, I said — and that’s one for years! The Ember Badge, and all eight lanterns lit in your honor.',
        ],
      ),
    ],
  },

  // ── Finale · "The One That Watched" (Mewtwo Beat E) · Auber of Team Meridian ──
  // Single, gentle stage: simply go sit at the shrine. No capture is ever required.
  // INTENDED EXTRAS (doc): the doc names codex-unlock the-reason + follower-cosmetic
  // (a shrine lantern); per the build brief the encoded reward is the chronicle page.
  {
    id: 'main-finale',
    kind: 'main',
    title: 'The One That Watched',
    giver: 'npc-meridian-auber',
    synopsis:
      'Auber asks only that you sit at the shrine and leave the berry out — whether it stays is its choice.',
    available: { kind: 'quest-done', ref: 'main-badge-8' },
    tone: ['warm', 'mysterious', 'tender'],
    reward: { kind: 'codex-unlock', entryId: 'the-circuit-walked' },
    stages: [
      stage(
        'stage-1',
        [{ kind: 'visit-biome', biomeId: 'town-shrine' }],
        [
          'We didn’t bring a single ball, you know. Just… come sit on the shrine step with the rest of us. Leave the berry out. And wait.',
        ],
        [
          'It watched you a long while, they say. The town rather hopes it’s found its reason to stay. Thank you — for being the kind of trainer it was looking for.',
        ],
      ),
    ],
  },
];
